/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
// eslint-disable-next-line import/no-extraneous-dependencies
import shortid from 'shortid';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import CancelIcon from '@material-ui/icons/Cancel';
import ScheduleIcon from '@material-ui/icons/Schedule';
import IconButton from '@material-ui/core/IconButton';
import EmojiTransportation from '@material-ui/icons/EmojiTransportation';
import CommuteIcon from '@material-ui/icons/Commute';
import Grid from '@material-ui/core/Grid';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';

import LoadingPageText from '../../../common/Loading/LoadingPageText';
import { checkFeatureAccess } from '../../../../utils/helpers';
import useTimeline from '../../timeline/useTimeline';
import globals from '../../../../globals/communityGlobals';
import {
  useFetchNonPatientAssignmentDetails,
  postUnassignClinicianFromNonPatientActivity,
} from '../../../../actions/areas/planner';
import RescheduleNonPatientActivity from './RescheduleNonPatientActivity';
import {
  startEndTimeDisplay,
  formatHours,
  parseShortDateTime,
  twelveHourTimeFormat,
  parseDateUtc,
} from '../../../../utils/dates';
import { UnassignmentSource } from '../../../../enums';

// eslint-disable-next-line react/prop-types
export default function NonPatientActivityDetails({ period, refreshGrid, isMileageCapEnabled }) {
  const {
    activeNonPatientAssignment: assignment,
    activeNonPatientAssignmentForReschedule,
    setActiveNonPatientAssignment,
    setActiveNonPatientAssignmentForReschedule,
    startLiveTrackingInterval,
    stopLiveTrackingInterval,
    setAlertMessage,
    resetContext,
    getAccessToken,
  } = useTimeline();

  const [shouldRescheduleForTeam, setShouldRescheduleForTeam] = useState(false);

  if (!assignment) return null;

  const { data: assignmentDetails, isDataLoading } = useFetchNonPatientAssignmentDetails(
    assignment.nonPatientActivityId,
    false,
  );
  const isNPAWithTravel = assignment.postcode !== null && assignment.postcode !== '';

  const resetAndReload = () => {
    resetContext();
    refreshGrid();
  };

  useEffect(() => {
    stopLiveTrackingInterval();
    return () => startLiveTrackingInterval();
  }, []);

  const faList = useSelector(store => store.global.auth.featureAccessList);
  const canAllocateWork = checkFeatureAccess(faList, globals.applicationFeatureAccesses.canAllocateWork);

  const unassignNPA = () => {
    const model = {
      assignGuid: assignment.assignGuid,
      postingId: assignment.postingId,
      unassignmentSource: UnassignmentSource.Manual,
      shouldCreateUnassignEventForInstance: true,
      startDate: assignment.totalTime.actual.startDate,
      endDate: assignment.totalTime.actual.endDate,
    };
    postUnassignClinicianFromNonPatientActivity(getAccessToken, model, resetAndReload, setAlertMessage);
  };

  const isNpaInFuture = parseDateUtc(period.startDate).isSameOrAfter(parseDateUtc(globals.now), 'day');
  const isNpaInFutureForToday =
    isNpaInFuture &&
    parseDateUtc(assignment.visitTime.actual.startDate).isSameOrAfter(parseDateUtc(globals.now), 'minute');

  const shouldShowBtnClass = classNames('sidePanel-header-action', {
    empty: !isNpaInFuture,
  });

  const headerRender = () => {
    const rescheduleNPABtn = () => {
      return (
        <ListItem
          className="header-action-button"
          key={'reschedule-NPA-btn'}
          button
          onClick={() => {
            setActiveNonPatientAssignmentForReschedule(assignment);
            setShouldRescheduleForTeam(false);
          }}
        >
          <ListItemIcon>
            <span className="icon-extend icon-reschedule" />
          </ListItemIcon>
          <ListItemText primary={'Reschedule'} />
        </ListItem>
      );
    };
    const rescheduleTeamNPABtn = () => {
      return (
        <ListItem
          className="header-action-button"
          key={'reschedule-team-NPA-btn'}
          button
          onClick={() => {
            setActiveNonPatientAssignmentForReschedule(assignment);
            setShouldRescheduleForTeam(true);
          }}
        >
          <ListItemIcon>
            <span className="icon-extend icon-reschedule" />
          </ListItemIcon>
          <ListItemText primary={'Reschedule For Team'} />
        </ListItem>
      );
    };
    const unassignNPABtn = () => {
      return (
        <ListItem className="header-action-button" key={'unassign-NPA-btn'} button onClick={() => unassignNPA()}>
          <ListItemIcon>
            <RemoveCircleIcon />
          </ListItemIcon>
          <ListItemText primary={'Unassign NPA'} />
        </ListItem>
      );
    };
    const editBtnRender = () => {
      return (
        <List component="nav" style={{ paddingTop: '0px', paddingBottom: '6px' }}>
          {canAllocateWork && isNpaInFuture ? rescheduleNPABtn() : <React.Fragment />}
          {canAllocateWork && isNpaInFuture && assignmentDetails && assignmentDetails.orgUnits.length > 0 ? (
            rescheduleTeamNPABtn()
          ) : (
            <React.Fragment />
          )}
          {isNpaInFuture && isNpaInFutureForToday ? unassignNPABtn() : <React.Fragment />}
        </List>
      );
    };

    return (
      <div className="sidePanel-header card-non-patient-activity">
        <div className="sidePanel-header-subtitle">
          <Box display="flex">
            <Box flexGrow={1} py={1}>
              <Typography variant="subtitle1">NonPatient activity</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => setActiveNonPatientAssignment(null)}>
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </div>
        <div className="sidePanel-header-wrapper">
          <div className="sidePanel-header-left-info-wrap">
            <div className="sidePanel-header-title">
              <Typography variant="h2">{assignment.primaryLabel}</Typography>
            </div>
            <div className="sidePanel-header-info patient-header">
              <Box className="assignment-desc-info" display="flex" flexDirection="row">
                <Box display="flex" flexDirection="column">
                  {isNPAWithTravel ? (
                    <Box className="header-icon-wrapper">
                      <span className="icon-extend icon-location" />
                      <span className="info-text">{assignment.postcode || ''}</span>
                    </Box>
                  ) : (
                    <React.Fragment />
                  )}
                  <Box className="header-icon-wrapper">
                    <span className="icon-extend icon-multiple-times" />
                    <span className="info-text">
                      {startEndTimeDisplay(assignment.visitTime.actual.startDate, assignment.visitTime.actual.endDate)}
                    </span>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column">
                  <Box className="header-icon-wrapper">
                    <ScheduleIcon />
                    <span className="info-text">{formatHours(assignment.visitTime.actual.duration)}</span>
                  </Box>
                  {isNPAWithTravel ? (
                    <Box className="header-icon-wrapper">
                      <DirectionsCarIcon />
                      <span className="info-text">
                        {assignment.travelTime ? (
                          startEndTimeDisplay(
                            assignment.travelTime.actual.startDate,
                            assignment.travelTime.actual.endDate,
                          )
                        ) : (
                          <React.Fragment />
                        )}
                      </span>
                    </Box>
                  ) : (
                    <React.Fragment />
                  )}
                </Box>
                {isNPAWithTravel ? (
                  <Box display="flex" flexDirection="column">
                    {isMileageCapEnabled && assignment.plannedTravelDistance !== null && (
                      <Box className="header-icon-wrapper">
                        <CommuteIcon />
                        <span className="info-text">{assignment.plannedTravelDistance}</span>
                      </Box>
                    )}
                    {isMileageCapEnabled && assignment.plannedTravelDistanceToBase !== null && (
                      <Box className="header-icon-wrapper">
                        <EmojiTransportation />
                        <span className="info-text">{assignment.plannedTravelDistanceToBase}</span>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <React.Fragment />
                )}
              </Box>
            </div>
          </div>
          <div className={shouldShowBtnClass}>{editBtnRender()}</div>
        </div>
      </div>
    );
  };

  const activityDetails = () => {
    return (
      <div className="clinician-info-box">
        <h6>Activity Details</h6>
        <List className="list-underline">
          <ListItem key="npasp-clinicians-item">
            <ListItemText primary="Clinicians" />
            <span className="divider" />
            <Grid container className="npa-clinician-list">
              {assignmentDetails.assignedTo.map(assigned => (
                <Grid item className="npa-clinician-list-item" key={shortid.generate()}>
                  {assigned}
                </Grid>
              ))}
            </Grid>
          </ListItem>
        </List>
      </div>
    );
  };

  const notes = () => {
    return (
      assignmentDetails.notes && (
        <div className="clinician-info-box">
          <h6>Notes</h6>
          <div className="treatment-description">{assignmentDetails.notes || '-'}</div>
        </div>
      )
    );
  };

  const activitySchedule = () => {
    return (
      <div className="clinician-info-box">
        <h6>Activity Schedule</h6>
        <List className="list-underline">
          <ListItem key="npasp-dateFrom-item">
            <ListItemText primary="Date From" />
            <span className="divider" />
            <ListItemText secondary={parseShortDateTime(assignmentDetails.dateFrom)} />
          </ListItem>
          <ListItem key="npasp-dateTo-item">
            <ListItemText primary="Date To" />
            <span className="divider" />
            <ListItemText secondary={parseShortDateTime(assignmentDetails.dateTo)} />
          </ListItem>
          <ListItem key="npasp-startTime-item">
            <ListItemText primary="Start Time" />
            <span className="divider" />
            <ListItemText secondary={twelveHourTimeFormat(assignmentDetails.startTime)} />
          </ListItem>
          <ListItem key="npasp-duration-item">
            <ListItemText primary="Duration" />
            <span className="divider" />
            <ListItemText secondary={formatHours(assignment.visitTime.actual.duration)} />
          </ListItem>
          <ListItem key="npasp-reccurence-item">
            <ListItemText primary="Recurrence Type" />
            <span className="divider" />
            <ListItemText secondary={assignmentDetails.recurrenceType} />
          </ListItem>
        </List>
      </div>
    );
  };

  const activityAddress = () => {
    return (
      <div className="clinician-info-box">
        <h6>Activity Address</h6>
        <List className="list-underline">
          <ListItem key="npasp-address1-item">
            <ListItemText primary="Address line 1" />
            <span className="divider" />
            <ListItemText secondary={assignmentDetails.address.addressLine1 || '-'} />
          </ListItem>
          <ListItem key="npasp-address2-item">
            <ListItemText primary="Address line 2" />
            <span className="divider" />
            <ListItemText secondary={assignmentDetails.address.addressLine2 || '-'} />
          </ListItem>
          <ListItem key="npasp-address3-item">
            <ListItemText primary="Address line 3" />
            <span className="divider" />
            <ListItemText secondary={assignmentDetails.address.addressLine3 || '-'} />
          </ListItem>
          <ListItem key="npasp-town-item">
            <ListItemText primary="Town/city" />
            <span className="divider" />
            <ListItemText secondary={assignmentDetails.address.townCity || '-'} />
          </ListItem>
          <ListItem key="npasp-postcode-item">
            <ListItemText primary="Postcode" />
            <span className="divider" />
            <ListItemText secondary={assignmentDetails.address.postCode || '-'} />
          </ListItem>
          <ListItem key="npasp-accessDetails-item">
            <ListItemText primary="Access Details" />
            <span className="divider" />
            <ListItemText secondary={assignmentDetails.address.accessDetails || '-'} />
          </ListItem>
        </List>
      </div>
    );
  };

  const activityDetailsRender = () => {
    return (
      <React.Fragment>
        <div className="sidePanel-content">
          <Grid container spacing={0}>
            <Grid item xs={12} sm={12}>
              {activityDetails()}
              {notes()}
              {activitySchedule()}
              {isNPAWithTravel ? activityAddress() : ''}
            </Grid>
          </Grid>
        </div>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <section className="side-panel-content-wrapper" id="non-patient-assignment-side-panel">
        <SwipeableDrawer
          anchor="right"
          open
          onOpen={() => {}}
          onClose={() => {}}
          ModalProps={{
            onBackdropClick: () => setActiveNonPatientAssignment(null),
          }}
        >
          <div className="sidePanel-wrapper" style={{ width: '635px' }}>
            {headerRender()}
            {!isDataLoading ? (
              activityDetailsRender()
            ) : (
              <LoadingPageText showHeader={false}>Wait a moment while we gather activity info...</LoadingPageText>
            )}
          </div>
        </SwipeableDrawer>
      </section>
      {activeNonPatientAssignmentForReschedule !== null && (
        <RescheduleNonPatientActivity toggleRefresh={refreshGrid} shouldRescheduleForTeam={shouldRescheduleForTeam} />
      )}
    </React.Fragment>
  );
}
